/**
 * Variant Picker — handles variant selection, updates price/URL/stock/gallery.
 * Buttons, swatches or dropdown; variants are read from a JSON script tag.
 * On change: updates variant ID, price, URL, ATC state, stock, gallery and label.
 */
import { Component } from '@theme/component';

class VariantPickerComponent extends Component {
  #variants = [];
  #variantMeta = {};
  #currentVariant = null;

  connectedCallback() {
    super.connectedCallback();

    const json = this.querySelector('[data-variants-json]');
    if (json) {
      try { this.#variants = JSON.parse(json.textContent); } catch { }
    }

    const metaJson = this.querySelector('[data-variant-metafields-json]');
    if (metaJson) {
      try { this.#variantMeta = JSON.parse(metaJson.textContent); } catch { }
    }

    this.#currentVariant = this.#getSelectedVariant();
  }

  /**
   * Called via on:change on radio inputs and selects
   */
  onOptionChange(event) {
    const variant = this.#getSelectedVariant();
    if (!variant) return;

    this.#currentVariant = variant;
    this.#updateSelectedLabels();
    this.#updateVariantId(variant);
    this.#updatePrice(variant);
    this.#updateURL(variant);
    this.#updateButton(variant);
    this.#updateStockIndicator(variant);
    this.#updateGallery(variant);
    this.#updateOptionAvailability();
  }

  /**
   * Gift-card amount picked from the "Autres montants" drawer: check the matching
   * radio, run the normal update, sync the drawer pills, then close the drawer.
   */
  selectAmount(event) {
    const btn = event.target.closest('[data-amount-value]');
    if (!btn) return;
    const value = btn.dataset.amountValue;
    const position = btn.dataset.amountOption;

    this.querySelectorAll(`input[type="radio"][name="option-${position}"]`).forEach((radio) => {
      radio.checked = radio.value === value;
    });

    const drawer = event.target.closest('dialog-component');
    if (drawer) {
      drawer.querySelectorAll('[data-amount-value]').forEach((pill) => {
        pill.dataset.selected = pill.dataset.amountValue === value ? 'true' : 'false';
      });
    }

    this.onOptionChange();
    if (drawer && typeof drawer.closeDialog === 'function') drawer.closeDialog();
  }

  /**
   * Build selected options array from checked radios or selects, find matching variant
   */
  #getSelectedVariant() {
    const options = [];
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const select = fieldset.querySelector('select');
      if (checked) {
        options.push(checked.value);
      } else if (select) {
        options.push(select.value);
      }
    });

    return this.#variants.find((v) => {
      return v.options.every((opt, i) => opt === options[i]);
    }) || null;
  }

  /**
   * Update the "selected value" text in legends (e.g., "Color: Blue")
   */
  #updateSelectedLabels() {
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const select = fieldset.querySelector('select');
      const value = checked?.value || select?.value;
      if (!value) return;

      const legend = fieldset.querySelector('legend');
      if (!legend) return;

      const position = checked?.name?.replace('option-', '') || select?.name?.replace('option-', '');
      const span = legend.querySelector(`[data-selected-value-${position}]`);
      if (span) span.innerHTML = ` <span class="tw:underline">${value}</span>`;
    });
  }

  #updateVariantId(variant) {
    const section = this.closest('.product-section') || document;
    const input = section.querySelector('[data-variant-id-input]');
    if (input) {
      input.value = variant.id;
      input.setAttribute('value', variant.id);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #updatePrice(variant) {
    const section = this.closest('.product-section') || document;
    const priceEl = section.querySelector('[data-price]');
    const compareEl = section.querySelector('[data-compare-price]');

    if (priceEl) {
      priceEl.textContent = this.#formatMoney(variant.price);
      priceEl.classList.add('shimmer-text');
      setTimeout(() => priceEl.classList.remove('shimmer-text'), 300);
    }

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = this.#formatMoney(variant.compare_at_price);
        compareEl.classList.remove('tw:hidden');
        priceEl?.classList.add('tw:text-error');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('tw:hidden');
        priceEl?.classList.remove('tw:text-error');
      }
    }
  }

  #updateURL(variant) {
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  #updateButton(variant) {
    const section = this.closest('.product-section') || document;
    const btn = section.querySelector('[data-add-to-cart]');
    if (!btn) return;

    const atcText = btn.querySelector('[data-atc-text]');
    if (atcText) {
      const label = variant.available
        ? (Theme.translations?.add_to_cart || 'Add to cart')
        : (Theme.translations?.sold_out || 'Sold out');
      atcText.textContent = `${label} - ${this.#formatMoney(variant.price)}`;
    }
    btn.disabled = !variant.available;
  }

  #updateStockIndicator(variant) {
    const section = this.closest('.product-section') || document;
    const indicator = section.querySelector('[data-stock-indicator]');
    if (!indicator) return;

    if (variant.available) {
      indicator.style.display = '';
    } else {
      indicator.querySelector('span:first-child')?.classList.replace('tw:bg-success', 'tw:bg-error');
      indicator.querySelector('span:last-child').textContent =
        Theme.translations?.sold_out || 'Sold out';
    }
  }

  #updateGallery(variant) {
    const gallery = document.querySelector('product-gallery-component');
    if (!gallery) return;

    // Check if variant has custom image gallery from metafields
    const meta = this.#variantMeta[String(variant.id)];
    if (meta && (meta.gallery?.length > 0 || meta.featured_image)) {
      const images = [];

      // Featured image: try metafield JSON first, then build from variant.featured_image
      if (meta.featured_image) {
        images.push(meta.featured_image);
      } else if (variant.featured_image) {
        const src = variant.featured_image.src;
        const base = src.split('?')[0];
        images.push({
          src_400: `${base}?width=400`,
          src_800: `${base}?width=800`,
          srcset: `${base}?width=400 400w, ${base}?width=600 600w, ${base}?width=800 800w, ${base}?width=1200 1200w, ${base}?width=1600 1600w`,
          zoom: `${base}?width=2000`,
          alt: variant.title || ''
        });
      }

      if (meta.gallery) images.push(...meta.gallery);
      if (images.length > 0 && typeof gallery.replaceGallery === 'function') {
        gallery.replaceGallery(images);
      }
      return;
    }

    // Fallback: scroll to variant featured media
    if (!variant.featured_media) return;

    const mediaEl = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
    if (mediaEl) {
      mediaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    if (typeof gallery.selectMediaById === 'function') {
      gallery.selectMediaById(variant.featured_media.id);
    }
  }

  /**
   * Mark unavailable option combinations (works for both radio buttons and swatches)
   */
  #updateOptionAvailability() {
    const fieldsets = [...this.querySelectorAll('fieldset')];
    const selectedOptions = [];

    fieldsets.forEach((fs) => {
      const checked = fs.querySelector('input:checked');
      const select = fs.querySelector('select');
      if (checked) selectedOptions.push(checked.value);
      else if (select) selectedOptions.push(select.value);
    });

    fieldsets.forEach((fieldset, position) => {
      // Handle radio-based buttons (les pastilles de couleur ne sont jamais barrées)
      const labels = fieldset.querySelectorAll('.variant-option');
      labels.forEach((label) => {
        const input = label.querySelector('input');
        if (!input) return;

        const hasAvailable = this.#variants.some((v) => {
          return v.options[position] === input.value && v.available;
        });

        label.classList.toggle('variant-option--unavailable', !hasAvailable);
      });

      // Handle dropdown options — disable unavailable values
      const select = fieldset.querySelector('select');
      if (select) {
        [...select.options].forEach((opt) => {
          const hasAvailable = this.#variants.some((v) => {
            return v.options[position] === opt.value && v.available;
          });
          opt.disabled = !hasAvailable;
          opt.textContent = opt.value + (hasAvailable ? '' : ' — Sold out');
        });
      }
    });
  }

  /**
   * Mirror of Shopify's standard money formatter — supports every placeholder
   * (amount, amount_no_decimals, *_with_comma_separator, *_with_apostrophe_separator, …)
   */
  #formatMoney(cents) {
    const format = Theme.moneyFormat || '{{amount}} €';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return format;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }
}

if (!customElements.get('variant-picker-component')) {
  customElements.define('variant-picker-component', VariantPickerComponent);
}
